import { stmtAclAllowEntity, stmtAddEnumValue, stmtAddPlugin, stmtAddPreview, stmtAddPreviewField, stmtAddRelation, stmtAddSchemaColumn, stmtAddStructureColumn, stmtAddStructureField, stmtCreateEntity, stmtCreateForm, stmtCreateInsideRepeaterForm, stmtCreateNavigation, stmtCreatePreview, stmtCreateRole, stmtCreateScope, stmtCreateView, stmtDefineClickEvent, stmtDefineRedirectOnPersistEvent, stmtOnDelete, stmtRemoveEnumValue, stmtSetUnique, } from '@contember-cloud/studio-dsl';
export const cmsTemplate = {
    name: 'My awesome CMS',
    type: 'cms',
    isDownloadable: true,
    intent: 'The CMS is a web application designed to allow users to easily create, manage, and publish content on their websites. It caters to content creators, small business owners, and individuals who need a straightforward way to manage their online presence.',
    specification: {
        create: {
            content: `## Application Analysis

1. Purpose: CMS is designed to provide a straightforward way for users to create, manage, and publish content on a website without requiring extensive technical knowledge.
2. Target Users: Content creators, small business owners, bloggers, and individuals who want to manage their website content easily.
3. Key Features:
   * Content creation and editing
   * Content organization (categories, tags)
   * Media management (images, videos)
   * Content publishing and scheduling

## User Flow Description

1. Core User Actions:
   * User registration and login
   * Create, edit, and delete content
   * Organize content into categories and add tags
   * Upload and manage media files
   * Publish or schedule content for publication
   * Manage user roles and permissions (for admin users)
2. Data Input and Submission:
   * Users input content through a rich text editor
   * Media files are uploaded through a file upload interface
   * Content metadata (title, description, categories, tags) is entered via form fields
   * User information is submitted through registration and profile update forms
3. Data Retrieval and Display:
   * Content is displayed in a list view for management purposes
   * Individual content items can be previewed or viewed as they would appear on the site
   * Media files are displayed in a gallery or file manager interface
   * User information is displayed in a profile view or user management interface
   * Content Management: List of all content with options to create, edit, or delete

## Summary

The CMS is a web application designed to allow users to easily create, manage, and publish content on their websites. It caters to content creators, small business owners, and individuals who need a straightforward way to manage their online presence.

The application's core features include user authentication, content creation and editing, content organization. The main user flows involve creating and managing content, organizing it with categories and tags, uploading media, and publishing or scheduling content.`,
        },
    },
    schema: {
        create: {
            dsl: `# Create entities
${stmtCreateEntity({ entityName: 'Page' })}
${stmtCreateEntity({ entityName: 'Article' })}
${stmtCreateEntity({ entityName: 'Author' })}
${stmtCreateEntity({ entityName: 'Category' })}
${stmtCreateEntity({ entityName: 'Tag' })}
${stmtCreateEntity({ entityName: 'Navigation' })}
${stmtCreateEntity({ entityName: 'NavigationItem' })}
${stmtCreateEntity({ entityName: 'Settings' })}

# Create enums
${stmtAddEnumValue({ enumName: 'navigationPosition', values: ['header', 'footer'] })}

# Add Page columns and relations
${stmtAddSchemaColumn({ entityName: 'Page', columnName: 'publishedAt', dataType: 'datetime' })}
${stmtAddSchemaColumn({ entityName: 'Page', columnName: 'archivedAt', dataType: 'datetime' })}
${stmtAddSchemaColumn({ entityName: 'Page', columnName: 'title', dataType: 'text', nullable: false })}

# Add Article columns and relations
${stmtAddSchemaColumn({ entityName: 'Article', columnName: 'publishedAt', dataType: 'datetime' })}
${stmtAddSchemaColumn({ entityName: 'Article', columnName: 'archivedAt', dataType: 'datetime' })}
${stmtAddSchemaColumn({ entityName: 'Article', columnName: 'title', dataType: 'text', nullable: false })}
${stmtAddRelation({
                owningEntity: 'Article',
                owningField: 'author',
                relationshipType: 'many to one',
                inverseEntity: 'Author',
                inverseField: 'articles',
            })}
${stmtOnDelete({ owningEntity: 'Article', owningField: 'author', action: 'restrict' })}
${stmtAddRelation({
                owningEntity: 'Article',
                owningField: 'category',
                relationshipType: 'many to one',
                inverseEntity: 'Category',
                inverseField: 'articles',
            })}
${stmtOnDelete({ owningEntity: 'Article', owningField: 'category', action: 'restrict' })}
${stmtAddRelation({ owningEntity: 'Article', owningField: 'tags', relationshipType: 'many to many', inverseEntity: 'Tag', inverseField: 'articles' })}

# Add Author columns and relations
${stmtAddSchemaColumn({ entityName: 'Author', columnName: 'name', dataType: 'text', nullable: false })}
${stmtAddSchemaColumn({ entityName: 'Author', columnName: 'bio', dataType: 'text' })}

# Add Category columns and relations
${stmtAddSchemaColumn({ entityName: 'Category', columnName: 'name', dataType: 'text', nullable: false })}
${stmtAddSchemaColumn({ entityName: 'Category', columnName: 'description', dataType: 'text' })}
${stmtSetUnique({ entityName: 'Category', fields: ['name'] })}

# Add Tag columns and relations
${stmtAddSchemaColumn({ entityName: 'Tag', columnName: 'name', dataType: 'text', nullable: false })}
${stmtSetUnique({ entityName: 'Tag', fields: ['name'] })}

# Add Navigation columns and relations
${stmtAddSchemaColumn({ entityName: 'Navigation', columnName: 'label', dataType: 'text' })}
${stmtAddSchemaColumn({ entityName: 'Navigation', columnName: 'position', dataType: 'navigationPosition', nullable: false })}
${stmtAddRelation({
                owningEntity: 'Navigation',
                owningField: 'items',
                relationshipType: 'one to many',
                inverseEntity: 'NavigationItem',
                inverseField: 'navigation',
            })}

# Add NavigationItem columns and relations
${stmtAddSchemaColumn({ entityName: 'NavigationItem', columnName: 'label', dataType: 'text', nullable: false })}

# Add image plugin
${stmtAddPlugin({ entityName: 'Author', pluginName: 'image', fieldName: 'image' })}
${stmtAddPlugin({ entityName: 'Article', pluginName: 'image', fieldName: 'image' })}
${stmtAddPlugin({ entityName: 'Settings', pluginName: 'image', fieldName: 'logo' })}

# Add richText plugin
${stmtAddPlugin({ entityName: 'Article', pluginName: 'richText', fieldName: 'content' })}

# Add seo plugin
${stmtAddPlugin({ entityName: 'Article', pluginName: 'seo', fieldName: 'seo' })}
${stmtAddPlugin({ entityName: 'Page', pluginName: 'seo', fieldName: 'seo' })}

# Add url plugin
${stmtAddPlugin({ entityName: 'Article', pluginName: 'url', fieldName: 'url', params: { derivedFrom: 'title' } })}
${stmtAddPlugin({ entityName: 'Page', pluginName: 'url', fieldName: 'url', params: { derivedFrom: 'title' } })}

# Add link plugin
${stmtAddPlugin({ entityName: 'NavigationItem', pluginName: 'link', fieldName: 'link' })}

# Add blocks to page
${stmtAddPlugin({
                entityName: 'Page',
                pluginName: 'blocks',
                fieldName: 'blocks',
                params: { blocks: [{ name: 'text', fields: [{ field: 'title', label: 'Title', type: 'string' }] }] },
            })}

# Add field to block
${stmtAddSchemaColumn({ entityName: 'Block', columnName: 'title', dataType: 'text' })}

# Add unique plugin
${stmtAddPlugin({ entityName: 'Settings', pluginName: 'unique' })}

# Acl
${stmtCreateRole({ roleName: 'public' })}
${stmtAclAllowEntity({ entityName: 'Page', roleNames: ['public'], accessTypes: ['read'] })}
${stmtAclAllowEntity({ entityName: 'Article', roleNames: ['public'], accessTypes: ['read'] })}
${stmtAclAllowEntity({ entityName: 'Category', roleNames: ['public'], accessTypes: ['read'] })}
${stmtAclAllowEntity({ entityName: 'Tag', roleNames: ['public'], accessTypes: ['read'] })}
${stmtAclAllowEntity({ entityName: 'Author', roleNames: ['public'], accessTypes: ['read'] })}
${stmtAclAllowEntity({ entityName: 'Content', roleNames: ['public'], accessTypes: ['read'] })}
${stmtAclAllowEntity({ entityName: 'ContentReference', roleNames: ['public'], accessTypes: ['read'] })}
${stmtAclAllowEntity({ entityName: 'Image', roleNames: ['public'], accessTypes: ['read'] })}
${stmtAclAllowEntity({ entityName: 'Url', roleNames: ['public'], accessTypes: ['read'] })}
${stmtAclAllowEntity({ entityName: 'Link', roleNames: ['public'], accessTypes: ['read'] })}
${stmtAclAllowEntity({ entityName: 'Seo', roleNames: ['public'], accessTypes: ['read'] })}
${stmtAclAllowEntity({ entityName: 'Block', roleNames: ['public'], accessTypes: ['read'] })}
${stmtAclAllowEntity({ entityName: 'BlockList', roleNames: ['public'], accessTypes: ['read'] })}
${stmtAclAllowEntity({ entityName: 'Settings', roleNames: ['public'], accessTypes: ['read'] })}

${stmtAddEnumValue({ enumName: 'contentReferenceTypeEnum', values: ['image'] })}
${stmtRemoveEnumValue({ enumName: 'contentReferenceTypeEnum', values: ['example'] })}
${stmtAddEnumValue({ enumName: 'blockType', values: ['text'] })}
${stmtRemoveEnumValue({ enumName: 'blockType', values: ['example'] })}
`,
        },
    },
    structure: {
        create: {
            dsl: `
# Create views
${stmtCreateView({ viewName: 'PageList' })}
${stmtCreateView({ viewName: 'PageCreate' })}
${stmtCreateView({ viewName: 'PageEdit' })}
${stmtCreateView({ viewName: 'ArticleList' })}
${stmtCreateView({ viewName: 'ArticleCreate' })}
${stmtCreateView({ viewName: 'ArticleEdit' })}
${stmtCreateView({ viewName: 'Settings' })}

# Create scopes
${stmtCreateScope({ scopeName: 'Page', viewName: 'PageList', entityName: 'Page', scopeType: 'dataGrid', filterQuery: '[archivedAt = null]' })}
${stmtCreateScope({ scopeName: 'Page', viewName: 'PageCreate', entityName: 'Page', scopeType: 'create' })}
${stmtCreateScope({ scopeName: 'Page', viewName: 'PageEdit', entityName: 'Page', scopeType: 'edit' })}
${stmtCreateScope({
                scopeName: 'Article',
                viewName: 'ArticleList',
                entityName: 'Article',
                scopeType: 'dataGrid',
                filterQuery: '[archivedAt = null]',
            })}
${stmtCreateScope({ scopeName: 'Article', viewName: 'ArticleCreate', entityName: 'Article', scopeType: 'create' })}
${stmtCreateScope({ scopeName: 'Article', viewName: 'ArticleEdit', entityName: 'Article', scopeType: 'edit' })}
${stmtCreateScope({ scopeName: 'Settings', viewName: 'Settings', entityName: 'Settings', scopeType: 'unique' })}
${stmtCreateScope({ scopeName: 'Navigation', viewName: 'Settings', entityName: 'Navigation', scopeType: 'multiEdit' })}

# Create Entity previews
${stmtCreatePreview({ entityName: 'Page', previewName: 'PagePreview' })}
${stmtAddPreviewField({ previewName: 'PagePreview', previewFieldName: 'title' })}
${stmtCreatePreview({ entityName: 'Article', previewName: 'ArticlePreview' })}
${stmtAddPreviewField({ previewName: 'ArticlePreview', previewFieldName: 'title' })}
${stmtCreatePreview({ entityName: 'Author', previewName: 'AuthorPreview' })}
${stmtAddPreviewField({ previewName: 'AuthorPreview', previewFieldName: 'name' })}
${stmtCreatePreview({ entityName: 'Category', previewName: 'CategoryPreview' })}
${stmtAddPreviewField({ previewName: 'CategoryPreview', previewFieldName: 'name' })}
${stmtCreatePreview({ entityName: 'Tag', previewName: 'TagPreview' })}
${stmtAddPreviewField({ previewName: 'TagPreview', previewFieldName: 'name' })}

# Create Entity forms
${stmtCreateForm({ formName: 'PageForm', entityName: 'Page' })}
${stmtAddStructureField({ formName: 'PageForm', fieldName: 'title' })}
${stmtAddStructureField({ formName: 'PageForm', fieldName: 'url', fieldType: 'plugin' })}
${stmtAddStructureField({ formName: 'PageForm', fieldName: 'publishedAt' })}
${stmtAddStructureField({ formName: 'PageForm', fieldName: 'blocks', fieldType: 'plugin' })}
${stmtAddStructureField({ formName: 'PageForm', fieldName: 'seo', fieldType: 'plugin' })}

${stmtCreateForm({ formName: 'ArticleForm', entityName: 'Article' })}
${stmtAddStructureField({ formName: 'ArticleForm', fieldName: 'title' })}
${stmtAddStructureField({ formName: 'ArticleForm', fieldName: 'url', fieldType: 'plugin' })}
${stmtAddStructureField({ formName: 'ArticleForm', fieldName: 'publishedAt' })}
${stmtAddStructureField({ formName: 'ArticleForm', fieldName: 'author', fieldType: 'select' })}
${stmtAddStructureField({ formName: 'ArticleForm', fieldName: 'category', fieldType: 'select' })}
${stmtAddStructureField({ formName: 'ArticleForm', fieldName: 'tags', fieldType: 'multiselect' })}
${stmtAddStructureField({ formName: 'ArticleForm', fieldName: 'image', fieldType: 'plugin' })}
${stmtAddStructureField({ formName: 'ArticleForm', fieldName: 'content', fieldType: 'plugin' })}
${stmtAddStructureField({ formName: 'ArticleForm', fieldName: 'seo', fieldType: 'plugin' })}

${stmtCreateForm({ formName: 'AuthorForm', entityName: 'Author' })}
${stmtAddStructureField({ formName: 'AuthorForm', fieldName: 'name' })}
${stmtAddStructureField({ formName: 'AuthorForm', fieldName: 'bio' })}
${stmtAddStructureField({ formName: 'AuthorForm', fieldName: 'image', fieldType: 'plugin' })}

${stmtCreateForm({ formName: 'CategoryForm', entityName: 'Category' })}
${stmtAddStructureField({ formName: 'CategoryForm', fieldName: 'name' })}
${stmtAddStructureField({ formName: 'CategoryForm', fieldName: 'description' })}

${stmtCreateForm({ formName: 'TagForm', entityName: 'Tag' })}
${stmtAddStructureField({ formName: 'TagForm', fieldName: 'name' })}

${stmtCreateForm({ formName: 'NavigationForm', entityName: 'Navigation' })}
${stmtAddStructureField({ formName: 'NavigationForm', fieldName: 'label' })}
${stmtAddStructureField({ formName: 'NavigationForm', fieldName: 'position' })}
${stmtAddStructureField({ formName: 'NavigationForm', fieldName: 'items', fieldType: 'repeater' })}

${stmtCreateForm({ formName: 'NavigationItemForm', entityName: 'NavigationItem' })}
${stmtAddStructureField({ formName: 'NavigationItemForm', fieldName: 'label' })}
${stmtAddStructureField({ formName: 'NavigationItemForm', fieldName: 'link', fieldType: 'plugin' })}
${stmtCreateInsideRepeaterForm({ formName: 'NavigationForm', fieldName: 'items', insideFormName: 'NavigationItemForm' })}

${stmtCreateForm({ formName: 'SettingsForm', entityName: 'Settings' })}
${stmtAddStructureField({ formName: 'SettingsForm', fieldName: 'logo', fieldType: 'plugin' })}

# Connect forms to scopes
${stmtCreateForm({ formName: 'PageForm', scopeName: 'Page', viewName: 'PageCreate', entityName: 'Page' })}
${stmtCreateForm({ formName: 'PageForm', scopeName: 'Page', viewName: 'PageEdit', entityName: 'Page' })}
${stmtCreateForm({ formName: 'ArticleForm', scopeName: 'Article', viewName: 'ArticleCreate', entityName: 'Article' })}
${stmtCreateForm({ formName: 'ArticleForm', scopeName: 'Article', viewName: 'ArticleEdit', entityName: 'Article' })}
${stmtCreateForm({ formName: 'SettingsForm', scopeName: 'Settings', viewName: 'Settings', entityName: 'Settings' })}
${stmtCreateForm({ formName: 'NavigationForm', scopeName: 'Navigation', viewName: 'Settings', entityName: 'Navigation' })}

# Add Columns to data grid
${stmtAddStructureColumn({ viewName: 'PageList', scopeName: 'Page', columnName: 'title' })}
${stmtAddStructureColumn({ viewName: 'PageList', scopeName: 'Page', columnName: 'publishedAt' })}

${stmtAddStructureColumn({ viewName: 'ArticleList', scopeName: 'Article', columnName: 'title' })}
${stmtAddStructureColumn({ viewName: 'ArticleList', scopeName: 'Article', columnName: 'publishedAt' })}
${stmtAddStructureColumn({ viewName: 'ArticleList', scopeName: 'Article', columnName: 'author' })}

# Connect previews to fields in forms
${stmtAddPreview({ previewName: 'AuthorPreview', formName: 'ArticleForm', fieldName: 'author', entityName: 'Author' })}
${stmtAddPreview({ previewName: 'CategoryPreview', formName: 'ArticleForm', fieldName: 'category', entityName: 'Category' })}
${stmtAddPreview({ previewName: 'TagPreview', formName: 'ArticleForm', fieldName: 'tags', entityName: 'Tag' })}

# Create navigation
${stmtCreateNavigation({ label: 'Pages', viewName: 'PageList', icon: 'edit-icon' })}
${stmtCreateNavigation({ label: 'Articles', viewName: 'ArticleList', icon: 'newspaper-icon' })}
${stmtCreateNavigation({ label: 'Settings', viewName: 'Settings', icon: 'settings' })}

# Add navigate events
${stmtDefineClickEvent({
                eventType: 'navigate',
                eventPosition: 'actions',
                sourceViewName: 'PageList',
                targetViewName: 'PageCreate',
                eventName: 'PageListToPageCreate',
                eventLabel: 'Create page',
            })}
${stmtDefineClickEvent({
                eventType: 'navigate',
                eventPosition: 'actions',
                sourceViewName: 'ArticleList',
                targetViewName: 'ArticleCreate',
                eventName: 'ArticleListToArticleCreate',
                eventLabel: 'Create article',
            })}
${stmtDefineClickEvent({
                eventType: 'navigate',
                eventPosition: 'entity',
                sourceViewName: 'PageList',
                sourceScopeName: 'Page',
                targetViewName: 'PageEdit',
                eventName: 'PageListToPageEdit',
                eventLabel: 'Edit page',
            })}
${stmtDefineClickEvent({
                eventType: 'navigate',
                eventPosition: 'entity',
                sourceViewName: 'ArticleList',
                sourceScopeName: 'Article',
                targetViewName: 'ArticleEdit',
                eventName: 'ArticleListToArticleEdit',
                eventLabel: 'Edit article',
            })}

# Add persist events
${stmtDefineClickEvent({
                eventType: 'persist',
                eventPosition: 'actions',
                sourceViewName: 'PageCreate',
                eventName: 'PageCreatePersist',
                eventLabel: 'Save',
            })}
${stmtDefineClickEvent({
                eventType: 'persist',
                eventPosition: 'actions',
                sourceViewName: 'PageEdit',
                eventName: 'PageEditPersist',
                eventLabel: 'Save',
            })}
${stmtDefineClickEvent({
                eventType: 'persist',
                eventPosition: 'actions',
                sourceViewName: 'ArticleCreate',
                eventLabel: 'Save',
                eventName: 'ArticleCreatePersist',
            })}
${stmtDefineClickEvent({
                eventType: 'persist',
                eventPosition: 'actions',
                sourceViewName: 'ArticleEdit',
                eventName: 'ArticleEditPersist',
                eventLabel: 'Save',
            })}
${stmtDefineClickEvent({
                eventType: 'persist',
                eventPosition: 'actions',
                sourceViewName: 'Settings',
                eventLabel: 'Save',
                eventName: 'SettingsPersist',
            })}

# Add redirect on persist events
${stmtDefineRedirectOnPersistEvent({
                sourceViewName: 'PageCreate',
                sourceScopeName: 'Page',
                targetViewName: 'PageEdit',
                eventName: 'PageCreateToPageEdit',
            })}
${stmtDefineRedirectOnPersistEvent({
                sourceViewName: 'ArticleCreate',
                sourceScopeName: 'Article',
                targetViewName: 'ArticleEdit',
                eventName: 'ArticleCreateToArticleEdit',
            })}

`,
        },
    },
    logs: [
        {
            create: {
                prompt: 'cms template',
                model: 'cms template',
                response: 'cms template',
                finishReason: 'cms template',
                promptTokens: 0,
                completionTokens: 0,
                totalTokens: 0,
            },
        },
    ],
};
//# sourceMappingURL=cms.js.map